/* Repository path: frontend/src/features/journey/ProfileEditPage.tsx */

import { useEffect, useState, type FormEvent } from "react";
import { ApiError } from "../../api/client";
import { apiToAnswers, editPlanProfile, fullAnswersToApi, getPlanProfile } from "../../api/planner";
import { Button } from "../../components/Button";
import { ChoiceGroup, type Choice } from "../../components/ChoiceGroup";
import { ErrorSummary } from "../../components/ErrorSummary";
import { LoadingState } from "../../components/LoadingState";
import { MultiChoiceGroup, type MultiChoice } from "../../components/MultiChoiceGroup";
import { StatusChip } from "../../components/StatusChip";
import { useContent } from "../../content/ContentContext";
import type { AnsweredDetailInput, Answers, FastProfileQuestion, SavedPlan } from "../../types";
import {
  clearInactive,
  conditionalRemovedAnnouncement,
  firstNewlyActiveField,
  isActive,
  newlyHiddenAnsweredFields,
  REVEAL_ANNOUNCEMENTS,
} from "./profileConditionals";

/**
 * W4-07 — one page, not a wizard. A person fixing one answer should not be
 * walked through five screens to reach it, and "show current answers" is a
 * review surface, not an onboarding one. Renders every field with the same
 * `ChoiceGroup`/`MultiChoiceGroup` and the same W4-01 content bundle the
 * first-run wizard uses — no local copy map here either. See
 * docs/design/features/W4-07.md.
 *
 * Deliberately never touches `sessionStorage` or `localStorage`: a saved
 * plan's answers must not outlive the tab, and this component holds its
 * own draft entirely in React state.
 */

function optionsToChoices(question: FastProfileQuestion | undefined): Choice[] {
  return (question?.options ?? []).map((option) => ({
    value: option.value,
    label: option.label,
    description: option.description,
    explainerKey: option.explainer_keys?.[0],
  }));
}

function optionsToMultiChoices(question: FastProfileQuestion | undefined): MultiChoice[] {
  return (question?.options ?? []).map((option) => ({
    value: option.value,
    label: option.label,
    description: option.description,
    explainerKey: option.explainer_keys?.[0],
  }));
}

function valueLabel(question: FastProfileQuestion | undefined, value: string): string {
  return question?.options?.find((option) => option.value === value)?.label ?? value;
}

function valuesLabel(question: FastProfileQuestion | undefined, values: string[]): string {
  return values.map((value) => valueLabel(question, value)).join(", ");
}

function arraysEqual(a: string[], b: string[]): boolean {
  if (a.length !== b.length) return false;
  const sortedA = [...a].sort();
  const sortedB = [...b].sort();
  return sortedA.every((value, index) => value === sortedB[index]);
}

function fieldChanged<K extends keyof Answers>(field: K, draft: Answers, original: Answers): boolean {
  const a = draft[field];
  const b = original[field];
  if (Array.isArray(a) || Array.isArray(b)) {
    return !arraysEqual((a as string[]) ?? [], (b as string[]) ?? []);
  }
  return a !== b;
}

/** A field whose Fast Profile field name intersects with what the user has
 * just changed, and which the plan has previously used a detail answer
 * for — the pre-save "we'll need to ask again about X" warning. */
function detailInputsAtRisk(
  changedFields: (keyof Answers)[],
  answeredDetailInputs: AnsweredDetailInput[],
  apiFieldNames: Record<string, string>,
): AnsweredDetailInput[] {
  const changedApiFields = new Set(changedFields.map((field) => apiFieldNames[field as string]));
  return answeredDetailInputs.filter((row) =>
    row.depends_on_profile_fields.some((field) => changedApiFields.has(field)),
  );
}

const CLIENT_TO_API_FIELD: Record<string, string> = {
  municipalityContext: "municipality_context",
  alreadyInNetherlands: "already_in_netherlands",
  arrivalDate: "arrival_date",
  stayOverFourMonths: "stay_over_four_months",
  registrationStatus: "registration_status",
  residenceCategory: "residence_category",
  permitExpiryDate: "permit_expiry_date",
  activities: "activities",
  recruitedBeforeArrival: "recruited_before_arrival",
  businessStatus: "business_status",
  housingType: "housing_type",
  studentAccommodationFacilities: "student_accommodation_facilities",
  householdType: "household_type",
  insuranceType: "insurance_type",
  assetIncomeFlags: "asset_income_flags",
  vehicleType: "vehicle_type",
};

const ALL_FIELDS: (keyof Answers)[] = [
  "municipalityContext",
  "alreadyInNetherlands",
  "arrivalDate",
  "stayOverFourMonths",
  "registrationStatus",
  "residenceCategory",
  "permitExpiryDate",
  "activities",
  "recruitedBeforeArrival",
  "businessStatus",
  "housingType",
  "studentAccommodationFacilities",
  "householdType",
  "insuranceType",
  "assetIncomeFlags",
  "vehicleType",
];

interface ProfileEditPageProps {
  /** The plan's current, committed Fast Profile — both the seed for the
   * draft and the baseline every "Changed" comparison is made against. */
  original: Answers;
  /** Saved-plan only. Absent on the preview path, where the client already
   * holds everything it needs and no extra fetch is made. */
  answeredDetailInputs?: AnsweredDetailInput[];
  busy: boolean;
  online: boolean;
  /** Server-side validation errors, keyed by client field name — same
   * shape `QuestionnairePage` already uses. */
  errors: Record<string, string>;
  onSave: (profile: Answers) => void;
  onCancel: () => void;
  onAnnounce: (message: string) => void;
}

export function ProfileEditPage({
  original,
  answeredDetailInputs = [],
  busy,
  online,
  errors,
  onSave,
  onCancel,
  onAnnounce,
}: ProfileEditPageProps) {
  const { status, questionsByField, retry } = useContent();
  const [draft, setDraft] = useState<Answers>(original);

  const q = (field: string) => questionsByField[field];

  const change = <K extends keyof Answers>(key: K, value: Answers[K]) => {
    setDraft((current) => {
      const provisional = { ...current, [key]: value };
      const next = clearInactive(provisional);
      const revealed = firstNewlyActiveField(current, next);
      if (revealed) {
        const legend = REVEAL_ANNOUNCEMENTS[revealed] ?? q(CLIENT_TO_API_FIELD[revealed])?.legend ?? "";
        onAnnounce(`Additional question shown: ${legend}`);
      } else {
        for (const field of newlyHiddenAnsweredFields(current, next)) {
          const legend = q(CLIENT_TO_API_FIELD[field])?.legend ?? field;
          onAnnounce(conditionalRemovedAnnouncement(field, legend));
        }
      }
      return next;
    });
  };

  const setMulti = (key: "activities" | "assetIncomeFlags", values: string[], message: string) => {
    change(key, values as Answers[typeof key]);
    if (message) onAnnounce(message);
  };

  if (status === "loading") {
    return (
      <div className="page page--narrow">
        <LoadingState label="Loading your answers" />
      </div>
    );
  }
  if (status === "error") {
    return (
      <div className="page page--narrow">
        <p className="eyebrow">Your answers</p>
        <h1>Check and change your answers</h1>
        <ErrorSummary errors={{ page: "We couldn’t load the questions. Check your connection and try again." }} />
        <Button onClick={retry}>Try again</Button>
      </div>
    );
  }

  const changedFields = ALL_FIELDS.filter(
    (field) => isActive(field, draft) && fieldChanged(field, draft, original),
  );
  const atRisk = detailInputsAtRisk(changedFields, answeredDetailInputs, CLIENT_TO_API_FIELD);

  const submit = (event: FormEvent) => {
    event.preventDefault();
    onSave(draft);
  };

  const changedChip = (field: keyof Answers) => {
    if (!changedFields.includes(field)) return null;
    const question = q(CLIENT_TO_API_FIELD[field]);
    const previous = original[field];
    const previousLabel = Array.isArray(previous)
      ? valuesLabel(question, previous as string[])
      : valueLabel(question, String(previous ?? ""));
    return (
      <p className="field-hint profile-edit__changed-note">
        <StatusChip tone="info">Changed</StatusChip>
        {previousLabel && <> Was: {previousLabel}</>}
      </p>
    );
  };

  return (
    <div className="page page--narrow profile-edit">
      <p className="eyebrow">Your answers</p>
      <h1>Check and change your answers</h1>
      <p>This updates the plan you already have. Nothing is saved until you choose to update it.</p>
      <ErrorSummary errors={errors} />
      {atRisk.length > 0 && (
        <div className="profile-edit__at-risk" role="status">
          <p className="profile-edit__eyebrow">What this will affect</p>
          <ul>
            {atRisk.map((row) => (
              <li key={row.code}>If you save this, we’ll need to ask you again about {row.label}.</li>
            ))}
          </ul>
        </div>
      )}
      <form className="profile-edit__form question-card" onSubmit={submit} noValidate>
        <fieldset className="profile-edit__group">
          <legend>Arrival and registration</legend>
          <ChoiceGroup
            id="edit-municipalityContext"
            legend={q("municipality_context").legend}
            choices={optionsToChoices(q("municipality_context"))}
            value={draft.municipalityContext}
            error={errors.municipalityContext}
            explainerKey={q("municipality_context").explainer_keys?.[0]}
            onChange={(value) => change("municipalityContext", value)}
          />
          {changedChip("municipalityContext")}
          <ChoiceGroup
            id="edit-alreadyInNetherlands"
            legend={q("already_in_netherlands").legend}
            choices={optionsToChoices(q("already_in_netherlands"))}
            value={draft.alreadyInNetherlands}
            error={errors.alreadyInNetherlands}
            onChange={(value) => change("alreadyInNetherlands", value)}
          />
          {changedChip("alreadyInNetherlands")}
          <div className={`field ${errors.arrivalDate ? "field--error" : ""}`}>
            <label htmlFor="edit-arrivalDate">{q("arrival_date").legend}</label>
            <p id="edit-arrivalDate-hint" className="field-hint">{q("arrival_date").hint}</p>
            {errors.arrivalDate && <p id="edit-arrivalDate-error" className="field-error">{errors.arrivalDate}</p>}
            <input
              id="edit-arrivalDate"
              type="date"
              value={draft.arrivalDate}
              aria-invalid={Boolean(errors.arrivalDate)}
              aria-describedby={`edit-arrivalDate-hint${errors.arrivalDate ? " edit-arrivalDate-error" : ""}`}
              onChange={(event) => change("arrivalDate", event.target.value)}
            />
          </div>
          {changedChip("arrivalDate")}
          <ChoiceGroup
            id="edit-stayOverFourMonths"
            legend={q("stay_over_four_months").legend}
            hint={q("stay_over_four_months").hint}
            choices={optionsToChoices(q("stay_over_four_months"))}
            value={draft.stayOverFourMonths}
            error={errors.stayOverFourMonths}
            onChange={(value) => change("stayOverFourMonths", value)}
          />
          {changedChip("stayOverFourMonths")}
          {isActive("registrationStatus", draft) && (
            <>
              <ChoiceGroup
                id="edit-registrationStatus"
                legend={q("registration_status").legend}
                hint={q("registration_status").hint}
                choices={optionsToChoices(q("registration_status"))}
                value={draft.registrationStatus}
                error={errors.registrationStatus}
                explainerKey={q("registration_status").explainer_keys?.[0]}
                onChange={(value) => change("registrationStatus", value)}
              />
              {changedChip("registrationStatus")}
            </>
          )}
        </fieldset>

        <fieldset className="profile-edit__group">
          <legend>Residence status</legend>
          <ChoiceGroup
            id="edit-residenceCategory"
            legend={q("residence_category").legend}
            choices={optionsToChoices(q("residence_category"))}
            value={draft.residenceCategory}
            error={errors.residenceCategory}
            explainerKey={q("residence_category").explainer_keys?.[0]}
            onChange={(value) => change("residenceCategory", value)}
          />
          {changedChip("residenceCategory")}
          <div className="field">
            <label htmlFor="edit-permitExpiryDate">{q("permit_expiry_date").legend}</label>
            <p id="edit-permitExpiryDate-hint" className="field-hint">{q("permit_expiry_date").hint}</p>
            {!draft.permitExpiryUnknown && (
              <input
                id="edit-permitExpiryDate"
                type="date"
                value={draft.permitExpiryDate}
                aria-describedby="edit-permitExpiryDate-hint"
                onChange={(event) => change("permitExpiryDate", event.target.value)}
              />
            )}
            <label className="check-choice">
              <input
                type="checkbox"
                checked={draft.permitExpiryUnknown}
                onChange={(event) => {
                  change("permitExpiryUnknown", event.target.checked);
                  if (event.target.checked) change("permitExpiryDate", "");
                }}
              />
              I don’t know the expiry date
            </label>
          </div>
          {changedChip("permitExpiryDate")}
        </fieldset>

        <fieldset className="profile-edit__group">
          <legend>Work and activities</legend>
          <MultiChoiceGroup
            id="edit-activities"
            legend={q("activities").legend}
            choices={optionsToMultiChoices(q("activities"))}
            values={draft.activities}
            error={errors.activities}
            explainerKey={q("activities").explainer_keys?.[0]}
            onChange={(values, message) => setMulti("activities", values, message)}
          />
          {changedChip("activities")}
          {isActive("recruitedBeforeArrival", draft) && (
            <>
              <ChoiceGroup
                id="edit-recruitedBeforeArrival"
                legend={q("recruited_before_arrival").legend}
                hint={q("recruited_before_arrival").hint}
                choices={optionsToChoices(q("recruited_before_arrival"))}
                value={draft.recruitedBeforeArrival}
                error={errors.recruitedBeforeArrival}
                onChange={(value) => change("recruitedBeforeArrival", value)}
              />
              {changedChip("recruitedBeforeArrival")}
            </>
          )}
          {isActive("businessStatus", draft) && (
            <>
              <ChoiceGroup
                id="edit-businessStatus"
                legend={q("business_status").legend}
                hint={q("business_status").hint}
                choices={optionsToChoices(q("business_status"))}
                value={draft.businessStatus}
                error={errors.businessStatus}
                explainerKey={q("business_status").explainer_keys?.[0]}
                onChange={(value) => change("businessStatus", value)}
              />
              {changedChip("businessStatus")}
            </>
          )}
        </fieldset>

        <fieldset className="profile-edit__group">
          <legend>Housing and household</legend>
          <ChoiceGroup
            id="edit-housingType"
            legend={q("housing_type").legend}
            hint={q("housing_type").hint}
            choices={optionsToChoices(q("housing_type"))}
            value={draft.housingType}
            error={errors.housingType}
            explainerKey={q("housing_type").explainer_keys?.[0]}
            onChange={(value) => change("housingType", value)}
          />
          {changedChip("housingType")}
          {isActive("studentAccommodationFacilities", draft) && (
            <>
              <ChoiceGroup
                id="edit-studentAccommodationFacilities"
                legend={q("student_accommodation_facilities").legend}
                hint={q("student_accommodation_facilities").hint}
                choices={optionsToChoices(q("student_accommodation_facilities"))}
                value={draft.studentAccommodationFacilities}
                error={errors.studentAccommodationFacilities}
                explainerKey={q("student_accommodation_facilities").explainer_keys?.[0]}
                onChange={(value) => change("studentAccommodationFacilities", value)}
              />
              {changedChip("studentAccommodationFacilities")}
            </>
          )}
          <ChoiceGroup
            id="edit-householdType"
            legend={q("household_type").legend}
            hint={q("household_type").hint}
            choices={optionsToChoices(q("household_type"))}
            value={draft.householdType}
            error={errors.householdType}
            explainerKey={q("household_type").explainer_keys?.[0]}
            onChange={(value) => change("householdType", value)}
          />
          {changedChip("householdType")}
        </fieldset>

        <fieldset className="profile-edit__group">
          <legend>Insurance, assets and vehicle</legend>
          <ChoiceGroup
            id="edit-insuranceType"
            legend={q("insurance_type").legend}
            hint={q("insurance_type").hint}
            choices={optionsToChoices(q("insurance_type"))}
            value={draft.insuranceType}
            error={errors.insuranceType}
            explainerKey={q("insurance_type").explainer_keys?.[0]}
            onChange={(value) => change("insuranceType", value)}
          />
          {changedChip("insuranceType")}
          <MultiChoiceGroup
            id="edit-assetIncomeFlags"
            legend={q("asset_income_flags").legend}
            hint={q("asset_income_flags").hint}
            choices={optionsToMultiChoices(q("asset_income_flags"))}
            values={draft.assetIncomeFlags}
            error={errors.assetIncomeFlags}
            explainerKey={q("asset_income_flags").explainer_keys?.[0]}
            onChange={(values, message) => setMulti("assetIncomeFlags", values, message)}
          />
          {changedChip("assetIncomeFlags")}
          <ChoiceGroup
            id="edit-vehicleType"
            legend={q("vehicle_type").legend}
            hint={q("vehicle_type").hint}
            choices={optionsToChoices(q("vehicle_type"))}
            value={draft.vehicleType}
            error={errors.vehicleType}
            explainerKey={q("vehicle_type").explainer_keys?.[0]}
            onChange={(value) => change("vehicleType", value)}
          />
          {changedChip("vehicleType")}
        </fieldset>

        <div className="action-row action-row--split">
          <Button type="button" variant="secondary" onClick={onCancel} disabled={busy}>Cancel</Button>
          <Button type="submit" busy={busy} disabled={!online}>
            {busy ? "Updating your plan…" : "Save and update my plan"}
          </Button>
        </div>
        {!online && <p className="field-hint">You’re offline. Reconnect to save your changes.</p>}
      </form>
    </div>
  );
}

interface SavedProfileEditPageProps {
  publicId: string;
  online: boolean;
  navigate: (path: string) => void;
  announce: (message: string) => void;
  onSaved: (result: SavedPlan) => void;
}

interface SavedProfileEditData {
  original: Answers;
  answeredDetailInputs: AnsweredDetailInput[];
  inputVersion: string;
}

/**
 * Self-contained saved-plan wrapper, mirroring `SavedRefinementPage`'s own
 * pattern in `RefinementPage.tsx`: fetches its own data (here,
 * `getPlanProfile`) rather than relying on a host component to already
 * have it, then renders the same `ProfileEditPage` the preview path uses,
 * with a saved-plan save handler.
 */
export function SavedProfileEditPage({
  publicId,
  online,
  navigate,
  announce,
  onSaved,
}: SavedProfileEditPageProps) {
  const [data, setData] = useState<SavedProfileEditData | null>(null);
  const [loadError, setLoadError] = useState("");
  const [busy, setBusy] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    let active = true;
    getPlanProfile(publicId)
      .then((response) => {
        if (!active) return;
        setData({
          original: apiToAnswers(response.profile),
          answeredDetailInputs: response.answered_detail_inputs,
          inputVersion: response.input_version,
        });
      })
      .catch((error: unknown) => {
        if (!active) return;
        setLoadError(
          error instanceof ApiError
            ? error.message
            : "We couldn’t load your answers right now. Try again.",
        );
      });
    return () => { active = false; };
  }, [publicId]);

  const save = async (profile: Answers) => {
    if (!data) return;
    setBusy(true);
    setErrors({});
    try {
      const result = await editPlanProfile(publicId, data.inputVersion, fullAnswersToApi(profile));
      onSaved(result);
    } catch (error) {
      if (error instanceof ApiError && error.code === "stale_input_version") {
        setErrors({
          page: "Your plan changed in another tab or window. Reload it to see the current answers before editing.",
        });
      } else if (error instanceof ApiError && error.fieldErrors) {
        setErrors(error.fieldErrors);
      } else {
        setErrors({ page: error instanceof ApiError ? error.message : "We couldn’t save your answers. Try again." });
      }
    } finally {
      setBusy(false);
    }
  };

  if (loadError) {
    return (
      <div className="page page--narrow">
        <ErrorSummary errors={{ page: loadError }} />
        <Button onClick={() => navigate(`/plans/${encodeURIComponent(publicId)}`)}>Back to your plan</Button>
      </div>
    );
  }
  if (!data) return <LoadingState label="Loading your answers" />;

  return (
    <ProfileEditPage
      original={data.original}
      answeredDetailInputs={data.answeredDetailInputs}
      busy={busy}
      online={online}
      errors={errors}
      onSave={save}
      onCancel={() => navigate(`/plans/${encodeURIComponent(publicId)}`)}
      onAnnounce={announce}
    />
  );
}
